<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { computed, reactive, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import ModalComponent from '@/components/ModalComponent.vue'
	import RoundedBox from '@/components/RoundedBox.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledInput from '@/components/StyledInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import { useAuth } from '@/composables/useAuth'
	import useDepartmentCostCenters from '@/composables/useDepartment.ts'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'
	import useInvalidateQueryData from '@/composables/useInvalidateQueryData'

	import { CostCenter } from '@/interfaces/costCenter'
	import { Department } from '@/interfaces/department'

	import useCompanyCostCenter from '@/modules/companies/composables/useCompanyCostCenter'
	import useCompanyDepartment from '@/modules/companies/composables/useCompanyDepartment'
	import useUser from '@/modules/users/composables/useUser'
	import { canEditProfile } from '@/modules/users/helpers/canEditProfile'
	import type { User } from '@/modules/users/interfaces/user'

	interface Props {
		user: User
	}

	const props = defineProps<Props>()
	const {
		updateUserAsync,
		updateUserLoading,
		refetch: refetchUser,
	} = useUser({
		immediate: false,
		params: {
			id: props.user.id,
		},
	})
	const { user: userLogged } = useAuth()
	const { hasPermissionUser } = useHasPermissionUser()
	const toast = useToast()

	const COMPANY = 'App\\Models\\Company'
	const canViewDepartments = computed(() => hasPermissionUser('view-departments', COMPANY))
	const canViewCostCenters = computed(() => hasPermissionUser('view-costCenters', COMPANY))
	const canUpdateDepartments = computed(() => hasPermissionUser('update-departments', COMPANY))
	const canUpdateCostCenters = computed(() => hasPermissionUser('update-costCenters', COMPANY))
	const canCreateDepartments = computed(() => hasPermissionUser('create-departments', COMPANY))
	const canCreateCostCenters = computed(() => hasPermissionUser('create-costCenters', COMPANY))

	interface Form {
		id: number
		departmentId: number | undefined
		costCenterId: number | undefined
	}

	const form = reactive<Form>({
		id: props.user.id,
		departmentId: props.user.departmentId,
		costCenterId: props.user.costCenterId,
	})

	const {
		departments,
		isLoading,
		isFetching,
		refetch: refetchDepartments,
		createDepartmentAsync,
		createDepartmentLoading,
	} = useCompanyDepartment({
		params: {
			companyId: props.user.companyId,
		},
		immediate: true,
	})

	// Solo la mutation de crear: la lista de CECOs del select viene de
	// useDepartmentCostCenters (por departamento), no de esta query.
	const { createCompanyCostCenterAsync, createCostCenterLoading } =
		useCompanyCostCenter()

	const {
		costCenters,
		refetch,
		departameniId,
		isLoading: isLoadingCostCenters,
		isFetching: isFetchingCostCenters,
	} = useDepartmentCostCenters({
		params: {
			id: 0,
		},
		immediate: false,
	})

	const departmentOptions = computed(() =>
		(departments.value || []).map((d: Department) => ({
			label: d.name,
			value: d.id,
		}))
	)

	const costCenterOptions = computed(() =>
		(costCenters.value || []).map((cc: CostCenter) => ({
			label: `${cc.key} — ${cc.name}`,
			value: cc.id,
		}))
	)

	const queryClient = useQueryClient()

	// Detectar cambios en el formulario
	const hasChanges = computed(() => {
		return (
			form.departmentId !== props.user.departmentId ||
			form.costCenterId !== props.user.costCenterId
		)
	})

	// Validaciones
	const isFormValid = computed(() => {
		return form.departmentId !== undefined
	})

	const onSubmit = async () => {
		try {
			await updateUserAsync(form)

			// Toast de éxito
			toast.success('¡Asignación organizacional actualizada correctamente!')
			refetchUser()
		} catch (error) {
			toast.error('Error al actualizar la asignación organizacional')
		}
	}

	watch(
		() => form.departmentId,
		() => {
			departameniId.value = form.departmentId!

			refetch()
		},
		{
			immediate: true,
		}
	)

	// ─── Creación rápida de departamento / CECO (con permiso) ──────────────────

	const showCreateDepartment = ref(false)
	const newDepartmentName = ref('')

	const showCreateCostCenter = ref(false)
	const newCostCenter = reactive<{
		key: string
		name: string
		departmentId: number | undefined
	}>({ key: '', name: '', departmentId: undefined })

	// Abre el modal con el departamento del form preseleccionado, pero editable.
	const openCreateCostCenter = () => {
		newCostCenter.departmentId = form.departmentId
		showCreateCostCenter.value = true
	}

	const handleCreateDepartment = async () => {
		const name = newDepartmentName.value.trim()
		if (!name) return

		try {
			await createDepartmentAsync({
				params: { name, companyId: props.user.companyId },
			})

			toast.success('Departamento creado correctamente')
			showCreateDepartment.value = false
			newDepartmentName.value = ''
			await refetchDepartments()

			// Preselecciona el recién creado (buscado por nombre: el POST puede
			// devolver el resource envuelto y no exponer el id directo).
			const created = (departments.value || []).find(
				(d: Department) => d.name === name
			)
			if (created) form.departmentId = created.id
		} catch (err: unknown) {
			const msg = (err as { response?: { data?: { message?: string } } })
				?.response?.data?.message
			toast.error(msg ?? 'Error al crear el departamento')
		}
	}

	const handleCreateCostCenter = async () => {
		const key = newCostCenter.key.trim()
		const name = newCostCenter.name.trim()
		if (!newCostCenter.departmentId || !key || !name) return

		try {
			await createCompanyCostCenterAsync({
				params: {
					name,
					key,
					companyId: props.user.companyId,
					departmentId: newCostCenter.departmentId,
				},
				config: {
					raiseToast: true,
					onSuccessToast: 'Centro de costos creado con éxito',
					onErrorToast: 'Error al crear centro de costos',
				},
			})

			showCreateCostCenter.value = false
			newCostCenter.key = ''
			newCostCenter.name = ''

			// El form apunta al departamento del CECO nuevo; el watch de
			// form.departmentId recarga la lista y aquí se preselecciona.
			form.departmentId = newCostCenter.departmentId
			await refetch()

			const created = (costCenters.value || []).find(
				(cc: CostCenter) => cc.key === key && cc.name === name
			)
			if (created) form.costCenterId = created.id
		} catch (err) {
			console.log(err)
		}
	}
</script>

<template>
	<div class="space-y-6">
		<!-- Form Section -->
		<form
			v-if="props.user && (canViewDepartments || canViewCostCenters)"
			@submit.prevent="onSubmit"
			class="space-y-6"
		>
			<RoundedBox class="bg-white">
				<div class="mb-6">
					<h3 class="text-lg font-medium text-gray-800 mb-1">
						Información Departamental
					</h3>
					<p class="text-sm text-gray-600">
						Selecciona el departamento y centro de costos correspondiente
					</p>
				</div>

				<div
					class="grid grid-cols-1 gap-6"
					:class="{ 'lg:grid-cols-2': canViewDepartments && canViewCostCenters }"
				>
					<!-- Department Selection -->
					<div
						v-if="canViewDepartments"
						class="space-y-2"
					>
						<div class="flex items-center gap-2 mb-3">
							<div
								class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center"
							>
								<svg
									class="w-4 h-4 text-blue-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
									></path>
								</svg>
							</div>
							<span class="text-sm font-medium text-gray-700"
								>Departamento</span
							>
							<span class="text-red-500 text-sm">*</span>

							<button
								v-if="canCreateDepartments"
								type="button"
								class="ml-auto inline-flex items-center gap-1 px-2.5 h-7 text-xs font-medium text-[#021F58] bg-[#021F58]/5 hover:bg-[#021F58]/10 rounded-lg transition-colors"
								@click="showCreateDepartment = true"
							>
								<span class="text-sm leading-none">+</span>
								Nuevo
							</button>
						</div>
						<StyledSelect
							:options="departmentOptions"
							v-model="form.departmentId"
							label="Departamento"
							:placeholder="
								isLoading || isFetching ? 'Cargando departamentos...'
								: departmentOptions.length === 0 ?
									'No hay departamentos disponibles'
								:	'Selecciona un departamento'
							"
							class="transition-all duration-200 focus-within:scale-[1.01]"
							:class="{
								'border-red-300 focus:border-red-500': !form.departmentId,
								'border-green-300 focus:border-green-500': form.departmentId,
							}"
							:loading-options="isLoading || isFetching"
							:disabled="isLoading || isFetching"
							is-required
						/>
						<p class="text-xs text-gray-500 mt-1">
							Departamento al que pertenece el usuario
						</p>
					</div>

					<!-- Cost Center Selection -->
					<div
						v-if="canViewCostCenters"
						class="space-y-2"
					>
						<div class="flex items-center gap-2 mb-3">
							<div
								class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center"
							>
								<svg
									class="w-4 h-4 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									></path>
								</svg>
							</div>
							<span class="text-sm font-medium text-gray-700"
								>Centro de Costos</span
							>

							<button
								v-if="canCreateCostCenters"
								type="button"
								class="ml-auto inline-flex items-center gap-1 px-2.5 h-7 text-xs font-medium text-[#021F58] bg-[#021F58]/5 hover:bg-[#021F58]/10 rounded-lg transition-colors"
								@click="openCreateCostCenter"
							>
								<span class="text-sm leading-none">+</span>
								Nuevo
							</button>
						</div>
						<StyledSelect
							:options="costCenterOptions"
							v-model="form.costCenterId"
							label="Centro de costos"
							:placeholder="
								!form.departmentId ? 'Primero selecciona un departamento'
								: isLoadingCostCenters || isFetchingCostCenters ?
									'Cargando centros de costos...'
								: costCenterOptions.length === 0 ?
									'No hay centros de costos para este departamento'
								:	'Selecciona un centro de costos'
							"
							class="transition-all duration-200 focus-within:scale-[1.01]"
							:loading-options="isLoadingCostCenters || isFetchingCostCenters"
							:disabled="!form.departmentId || isLoading || isFetching"
						/>
						<p class="text-xs text-gray-500 mt-1">
							Centro de costos para la asignación presupuestaria
						</p>
					</div>
				</div>

				<!-- Info Section -->
				<div
					class="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg"
				>
					<div class="flex">
						<div class="flex-shrink-0">
							<svg
								class="w-5 h-5 text-blue-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								></path>
							</svg>
						</div>
						<div class="ml-3">
							<h3 class="text-sm font-medium text-blue-800">
								Información importante
							</h3>
							<div class="mt-2 text-sm text-blue-700">
								<p>
									En caso de no encontrar el departamento deseado en la lista,
									por favor solicita al Travel Manager o Master que lo agregue
									al sistema.
								</p>
							</div>
						</div>
					</div>
				</div>
			</RoundedBox>

			<!-- Action Section -->
			<RoundedBox class="bg-white">
				<div
					class="flex flex-col sm:flex-row gap-4 justify-between items-center"
				>
					<div class="text-sm text-gray-600">
						<span
							v-if="updateUserLoading"
							class="flex items-center gap-2"
						>
							<svg
								class="animate-spin h-4 w-4 text-blue-500"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4"
								></circle>
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								></path>
							</svg>
							Guardando cambios...
						</span>
						<span
							v-else-if="!hasChanges"
							class="text-green-600 flex items-center gap-2"
						>
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M5 13l4 4L19 7"
								></path>
							</svg>
							Todos los cambios guardados
						</span>
						<span
							v-else-if="!isFormValid"
							class="text-red-600"
						>
							⚠️ Selecciona un departamento para continuar
						</span>
						<span
							v-else
							class="text-amber-600"
						>
							📝 Hay cambios pendientes
						</span>
					</div>
					<div class="flex gap-3">
						<StyledButton
							v-if="
								canEditProfile(userLogged?.id!, props.user.id) ||
								hasPermissionUser('update-users', 'App\\Models\\Company') ||
								canUpdateDepartments ||
								canUpdateCostCenters
							"
							:label="
								updateUserLoading ? 'Guardando...'
								: !hasChanges ? 'Sin Cambios'
								: 'Guardar Cambios '
							"
							:class="
								!hasChanges || !isFormValid ?
									'bg-gray-300 text-gray-500 cursor-not-allowed'
								:	'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 shadow-lg'
							"
							type="submit"
							:loading="updateUserLoading"
							:disabled="updateUserLoading || !hasChanges || !isFormValid"
						/>
					</div>
				</div>
			</RoundedBox>
		</form>

		<!-- Modal: crear departamento. Sin @update:model-value: no se cierra con
		     click fuera ni ESC, solo con sus botones. -->
		<ModalComponent
			:model-value="showCreateDepartment"
			class="max-w-md !rounded-2xl"
		>
			<template #modalBody>
				<div class="p-6">
					<div class="flex items-center gap-3 mb-5">
						<div
							class="flex items-center justify-center w-10 h-10 rounded-xl bg-[#021F58]/5 text-[#021F58]"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									d="M12 6v6m0 0v6m0-6h6m-6 0H6"
								/>
							</svg>
						</div>
						<div>
							<h3 class="text-base font-bold text-gray-900">
								Nuevo departamento
							</h3>
							<p class="text-sm text-gray-500">
								Se asignará al usuario al crearse
							</p>
						</div>
					</div>

					<StyledInput
						v-model="newDepartmentName"
						label="Nombre del departamento"
						placeholder="ej. Recursos Humanos, Ventas, Marketing..."
						@keydown.enter="handleCreateDepartment"
					/>

					<div class="flex justify-end gap-2 mt-6">
						<button
							type="button"
							class="inline-flex items-center px-4 h-9 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
							@click="
								() => {
									showCreateDepartment = false
									newDepartmentName = ''
								}
							"
						>
							Cancelar
						</button>

						<button
							type="button"
							:disabled="!newDepartmentName.trim() || createDepartmentLoading"
							class="inline-flex items-center px-4 h-9 text-sm font-semibold text-white bg-[#021F58] hover:bg-[#1C3A7A] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
							@click="handleCreateDepartment"
						>
							{{ createDepartmentLoading ? 'Creando...' : 'Crear departamento' }}
						</button>
					</div>
				</div>
			</template>
		</ModalComponent>

		<!-- Modal: crear centro de costos -->
		<ModalComponent
			:model-value="showCreateCostCenter"
			class="max-w-md !rounded-2xl"
		>
			<template #modalBody>
				<div class="p-6">
					<div class="flex items-center gap-3 mb-5">
						<div
							class="flex items-center justify-center w-10 h-10 rounded-xl bg-[#021F58]/5 text-[#021F58]"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									d="M12 6v6m0 0v6m0-6h6m-6 0H6"
								/>
							</svg>
						</div>
						<div>
							<h3 class="text-base font-bold text-gray-900">
								Nuevo centro de costos
							</h3>
							<p class="text-sm text-gray-500">
								Se creará en el departamento seleccionado y se asignará al
								usuario
							</p>
						</div>
					</div>

					<div class="space-y-4">
						<div class="grid grid-cols-2 gap-4">
							<StyledInput
								v-model="newCostCenter.key"
								label="Clave"
								placeholder="ej. 816"
								@keydown.enter="handleCreateCostCenter"
							/>

							<StyledInput
								v-model="newCostCenter.name"
								label="Nombre CECO"
								placeholder="ej. Análisis"
								@keydown.enter="handleCreateCostCenter"
							/>
						</div>

						<StyledSelect
							v-model="newCostCenter.departmentId"
							:options="departmentOptions"
							label="Departamento"
							:placeholder="
								departmentOptions.length === 0 ?
									'No hay departamentos disponibles'
								:	'Selecciona un departamento'
							"
							:loading-options="isLoading || isFetching"
							:disabled="isLoading || isFetching"
							is-required
						/>

						<div class="flex justify-end gap-2 pt-2">
							<button
								type="button"
								class="inline-flex items-center px-4 h-9 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
								@click="
									() => {
										showCreateCostCenter = false
										newCostCenter.key = ''
										newCostCenter.name = ''
									}
								"
							>
								Cancelar
							</button>

							<button
								type="button"
								:disabled="
									!newCostCenter.departmentId ||
									!newCostCenter.key.trim() ||
									!newCostCenter.name.trim() ||
									createCostCenterLoading
								"
								class="inline-flex items-center px-4 h-9 text-sm font-semibold text-white bg-[#021F58] hover:bg-[#1C3A7A] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
								@click="handleCreateCostCenter"
							>
								{{
									createCostCenterLoading ? 'Creando...' : (
										'Crear centro de costos'
									)
								}}
							</button>
						</div>
					</div>
				</div>
			</template>
		</ModalComponent>
	</div>
</template>

<style scoped>
	/* Animaciones suaves para las transiciones */
	.transition-all {
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	}

	/* Efecto hover para los campos de entrada */
	.focus-within\:scale-\[1\.01\]:focus-within {
		transform: scale(1.01);
	}

	/* Estilo personalizado para el botón principal */
	.transform:hover {
		transform: scale(1.05);
	}

	/* Sombras suaves */
	.shadow-lg {
		box-shadow:
			0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
	}

	/* Animación de carga */
	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	.animate-spin {
		animation: spin 1s linear infinite;
	}

	/* Espaciado mejorado */
	.space-y-6 > :not([hidden]) ~ :not([hidden]) {
		margin-top: 1.5rem;
	}

	/* Responsive improvements */
	@media (max-width: 1024px) {
		.grid-cols-1.lg\:grid-cols-2 {
			grid-template-columns: 1fr;
		}
	}

	@media (max-width: 640px) {
		.flex-col.sm\:flex-row {
			flex-direction: column;
		}
	}

	/* Gradient background animation */
	.bg-gradient-to-r {
		background-size: 200% 200%;
		animation: gradient-shift 3s ease infinite;
	}

	@keyframes gradient-shift {
		0% {
			background-position: 0% 50%;
		}
		50% {
			background-position: 100% 50%;
		}
		100% {
			background-position: 0% 50%;
		}
	}
</style>
